<template>

    <div class="calculator-overlay">
        <div class="results-box" v-if="rlv !== null && rlv >= 250">
            <div class="stats-container">
                <div class="stat">
                    <span class="unitnumber">{{ potentialNewUnits }}</span>
                    <span class="label">UNITS PRODUCED</span>
                </div>
                <div class="stat">
                    <span class="affnumber">{{ affordableUnits }}</span>
                    <span class="label">AFFORDABLE</span>
                </div>
            </div>
        </div>
        <div class="noresults-box" v-else>
            <span class="no-units">0</span>
            <br>
            <span class="conversion-notfeasible">CONVERSION NOT FEASIBLE</span>
            <br>
            <span class="adjust-sliders">ADJUST SLIDERS</span>

        </div>
        <div class="calculator">
            <label for="rbaInput">Total Selected Square Footage: </label>
            <input v-model="formattedRba" id="rbaInput" type="text" placeholder="Enter RBA value"
                style="font-family: 'Barlow Condensed', sans-serif; font-weight: 400; font-size: 15pt;">
            <!-- Occupancy Slider -->
            <label for="occupancy">Percent Affordable: {{ selectedOccupancy }}%</label>
            <input type="range" id="occupancy" v-model="selectedOccupancy" min="0" max="100" step="5">
            <!-- AMI Level Slider -->
            <label for="amiLevel">AMI Level: {{ selectedAmiLabel }}</label>
            <input type="range" id="amiLevel" v-model="amiIndex" :min="0" :max="amiLabels.length - 1" step="1">
            <label for="subsidy">Subsidy: ${{ subsidy }} million</label>
            <input type="range" id="subsidy" v-model="subsidy" min="0" max="1000" step="10">
        </div>
    </div>
</template>

<script>
export default {
    name: 'Calculator',
    props: {
        rba: Number // Define 'rba' as a prop
    },
    data() {
        return {
            // Initialize local copy from the prop
            rlv: null,
            localRba: 0, // Local copy of RBA
            selectedOccupancy: 0,
            amiIndex: 0,
            subsidy: 1,  // Initial subsidy set to $100 million
            rents: [],
            amiLevels: [], // You can keep this if you need to store additional data dynamically
        };

    },

    watch: {
        rba(newVal) {
            this.localRba = newVal; // Update local copy when prop changes
            this.calculateRLV(); // Recalculate RLV when RBA changes
        },
        localRba() {
            this.calculateRLV(); // Recalculate when local RBA changes
        },
        selectedOccupancy(newVal) {
            this.calculateRLV(); // Recalculate when occupancy changes
            this.updateSliderColor('occupancy', newVal);
        },
        amiIndex(newVal) {
            this.calculateRLV(); // Recalculate when AMI index changes
            this.updateSliderColor('amiLevel', newVal);
        },
        subsidy(newVal) {
            this.calculateRLV(); // Recalculate when subsidy changes
            this.updateSliderColor('subsidy', newVal);
        },

    },
    created() {
        this.fetchRents();
    },
    computed: {
        amiLabels() {
            // Assuming the structure of your JSON, pick one entry (e.g., the first one) to get the keys
            return this.rents.length > 0 ? Object.keys(this.rents[0]).filter(key => key.includes('% AMI')) : [];
        },
        selectedAmiLabel() {
            return this.amiLabels[this.amiIndex] || '';
        },
        rent() {
            let occupancyData = this.rents.find(item => item.FIELD1 === `${this.selectedOccupancy}.00%`);
            if (!occupancyData) return null;
            return occupancyData[this.selectedAmiLabel];
        },
        potentialNewUnits() {
            if (this.rlv !== null && this.rlv >= 250) {
                return Math.floor(this.localRba / 920);
            } else {
                return 0;
            }
        },
        affordableUnits() {
            return Math.floor(this.potentialNewUnits * (this.selectedOccupancy / 100)); // Calculate affordable units based on occupancy percentage
        },
        formattedRba: {
            get() {
                // Format the number with commas when reading it
                return this.localRba.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            },
            set(value) {
                // Remove all non-digits when updating the localRba
                this.localRba = parseInt(value.replace(/,/g, ''), 10) || 0;
            }
        }
    },
    methods: {
        async fetchRents() {
            try {
                const response = await fetch('rents.json'); // Adjust the path if necessary
                this.rents = await response.json();
            } catch (error) {
                console.error("Failed to load rents:", error);
            }
        },
        calculateRLV() {
            if (!this.rent) return; // Exit if rent isn't loaded correctly
            const taxes = this.localRba * 128 * 0.1227;
            const revenue = this.localRba * 0.8 * this.rent;
            const noi = revenue * 0.95 * 0.75 - taxes;
            const grossValue = noi / 0.055 + this.subsidy * 1000000;
            const hardCosts = this.localRba * 366 * 1.1;
            const softCosts = hardCosts * 0.2 * 1.1;
            const leasing = revenue * 0.95 * 0.15;
            const financing = (hardCosts + softCosts) * 0.1;
            const profit = hardCosts * 0.05;
            const totalCosts = hardCosts + softCosts + leasing + financing + profit;
            this.rlv = (grossValue - totalCosts) / this.localRba;
        },
        updateSliderColor(sliderName, value) {
            let max = 1000; // Maximum value for the subsidy slider
            let percentage = (value / max) * 100;
            if (sliderName === 'occupancy') {
                max = 100; // Maximum value for the occupancy slider
            } else if (sliderName === 'amiLevel') {
                max = this.amiLabels.length - 1; // Maximum value for the amiLevel slider
            }
            percentage = (value / max) * 100;
            const color = `linear-gradient(to right, #fcd581 ${percentage}%, #ddd ${percentage}%)`;
            document.documentElement.style.setProperty(`--${sliderName}-slider-track-color`, color);
        },

    },
    mounted() {
        this.updateSliderColor('occupancy', this.selectedOccupancy); // Initial color update for occupancy slider
        this.updateSliderColor('amiLevel', this.amiIndex); // Initial color update for amiLevel slider
        this.updateSliderColor('subsidy', this.subsidy); // Initial color update for subsidy slider
    }
};
</script>

<style>
.calculator-overlay {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 600px;
    margin: 40px auto;
}

.results-box {
    width: 350px;
    height: 180px;
    padding: 20px;
    text-align: center;
    background-color: rgba(252, 213, 129, 0.75);
    border: 1px solid #ccc;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    border-radius: 8px;

    /* Increase font size to 20pt */
}

.noresults-box {
    width: 350px;
    height: 180px;
    padding: 20px;
    text-align: center;
    background-color: rgba(252, 213, 129, 0.75);
    border: 1px solid #ccc;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    border-radius: 8px;

    /* Increase font size to 20pt */
}

.results-box h2 {
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 500;
    font-size: 15pt;
}

.calculator {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 350px;
    height: 250px;
    margin: 40px auto;
    padding: 20px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    text-align: center;
    font-family: Arial, sans-serif;
    background-color: rgba(166, 216, 243, 0.5);
    justify-content: space-evenly;
    border-radius: 8px;
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 500;
    font-size: 15pt;

}

input[id="occupancy"] {
    -webkit-appearance: none;
    width: 100%;
    height: 5px;
    background: var(--occupancy-slider-track-color, #ffffff);
    outline: none;
    opacity: 1;
    -webkit-transition: .2s;
    transition: opacity .2s;
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 500;
    font-size: 15pt;
}

input[id="amiLevel"] {
    -webkit-appearance: none;
    width: 100%;
    height: 5px;
    background: var(--amiLevel-slider-track-color, #ffffff);
    /* Change the variable name */
    outline: none;
    opacity: 1;
    -webkit-transition: .2s;
    transition: opacity .2s;
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 500;
    font-size: 15pt;
}

input[id="subsidy"] {
    -webkit-appearance: none;
    width: 100%;
    height: 5px;
    background: var(--subsidy-slider-track-color, #ffffff);
    /* Change the variable name */
    outline: none;
    opacity: 1;
    -webkit-transition: .2s;
    transition: opacity .2s;
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 500;
    font-size: 15pt;
}

input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #FFFFFF;
    border: 4px solid #FFC600;
    cursor: pointer;
    font-weight: 500;
}

.results-box .label {
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 400;
    font-size: 15pt;
}

.calculator label {
    width: 90%;
    text-align: center;
}

h2 {
    margin: 0;
    color: #333;
    font-size: 18px;
}


.potential-units {

    color: rgb(33, 174, 122);
    font-size: 60px;
    animation: countUp 1s steps(10, end) forwards;
    /* Adjust duration and steps as needed */



}

.affordable-units {
    color: rgb(33, 174, 122);
    font-size: 60px;
    animation: countUp 1s steps(10, end) forwards;
    /* Adjust duration and steps as needed */


}



.no-units {
    color: rgb(209, 49, 49);
    font-size: 80px;
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 500;

}

.adjust-sliders {

    color: rgb(209, 49, 49);
    font-size: 25;
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 500;

}

.conversion-notfeasible {

    color: rgb(209, 49, 49);
    font-size: 30px;
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 500;

}



.stats-container {
    display: flex;
    justify-content: space-around;
    /* This spreads out the stat blocks */
}

.stat {
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 500;
    font-size: 40pt;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 20px;
}

.unitnumber {
    color: rgb(33, 174, 122);
    font-size: 120px;
    animation: countUp 1s steps(10, end) forwards;
    /* Adjust duration and steps as needed */
}

.affnumber {
    margin-top: 37px;
    color: rgb(110, 200, 167);
    font-size: 89px;
    animation: countUp 1s steps(10, end) forwards;
    /* Adjust duration and steps as needed */
}



@media (max-width: 600px) {
    .stats-container {
        flex-direction: column;
        /* Stack them vertically on small screens */
    }
}
</style>
